/**
 * @file apps/web/src/pages/SessionDetailPage.tsx
 * @description UAT session detail with three tabs:
 *   Requirements — transcript and extracted requirements from the audio pipeline
 *   Test Scripts — ScriptTable for scripts linked to this session
 *   FDD          — FDDEditor sections if a document is linked
 * Route: /sessions/:sessionId
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, ClipboardList, FileText, Layers, AlertCircle,
  CheckCircle, Clock, Play, ChevronRight,
} from 'lucide-react';
import { useAuth } from '../lib/auth.js';
import { uatApi, testScriptsApi, documentsApi, audioApi } from '../lib/api.js';
import { ScriptTable, type TestScript } from '../components/ScriptTable.js';
import { FDDEditor, type FDDSection } from '../components/FDDEditor.js';
import { Badge } from '../components/Badge.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UatSession {
  id:            string;
  name:          string;
  status:        string;
  project_id:    string;
  created_at:    string;
  scheduled_at?: string | null;
  started_at?:   string | null;
  completed_at?: string | null;
}

// Wire shape of a GET /audio?projectId= row — audio_uploads columns plus
// the LEFT JOINed transcriptions row (apps/api/src/routes/audio.ts). There
// is no session_id anywhere in this schema (audio_uploads/transcriptions
// are project-scoped only, see migration 009) — a UAT session has no real
// FK to a specific upload, so this tab shows the project's most recent
// completed transcription rather than "this session's" transcription.
interface AudioUpload {
  id:                  string;
  project_id:          string;
  filename:            string;
  duration_secs:       number | null;
  uploaded_at:         string;
  transcription_status: string | null;   // pending|processing|completed|failed
  transcript_text?:    string | null;
  language?:           string | null;
}

interface Requirement {
  id:      string;
  text:    string;
  source?: string | null;
}

type ActiveTab = 'requirements' | 'scripts' | 'fdd';

// ---------------------------------------------------------------------------
// Requirements tab
// ---------------------------------------------------------------------------

function RequirementsTab({
  upload,
  loading,
}: {
  upload:  AudioUpload | null;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="space-y-3 animate-pulse">
        {[1,2,3].map((i) => <div key={i} className="card h-12 bg-gray/60" />)}
      </div>
    );
  }

  if (!upload) {
    return (
      <div className="empty-state py-12">
        <Layers size={24} className="text-muted/40 mb-2 mx-auto" />
        <p className="text-sm text-muted">No audio has been uploaded for this project yet.</p>
        <p className="text-xs text-muted/70 mt-1">
          Upload one from the project's Audio page to extract requirements.
        </p>
      </div>
    );
  }

  if (upload.transcription_status !== 'completed') {
    return (
      <div className="card p-5 text-center">
        <Clock size={20} className="text-muted/40 mb-2 mx-auto" />
        <p className="text-sm text-muted">
          Transcription status:{' '}
          <span className="font-mono text-ink">{upload.transcription_status ?? 'pending'}</span>
        </p>
        <p className="text-xs text-muted/70 mt-1">Requirements will appear when processing completes.</p>
      </div>
    );
  }

  const lines = (upload.transcript_text ?? '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted -mb-1">
        Showing the project's most recent completed transcription — UAT sessions
        aren't individually linked to a specific audio upload in this build.
      </p>

      {/* Pipeline summary */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle size={14} className="text-success" />
          <p className="text-sm font-medium text-ink">{upload.filename}</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px] font-mono text-muted">
          {upload.language && (
            <div>
              <p className="text-[10px] uppercase tracking-wide mb-0.5">Language</p>
              <p className="text-ink font-medium">{upload.language}</p>
            </div>
          )}
          {upload.duration_secs != null && (
            <div>
              <p className="text-[10px] uppercase tracking-wide mb-0.5">Duration</p>
              <p className="text-ink font-medium">
                {Math.floor(upload.duration_secs / 60)}m {upload.duration_secs % 60}s
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Transcript */}
      {lines.length > 0 && (
        <div className="card p-5">
          <h3 className="font-display text-sm font-semibold text-ink mb-3">Transcript</h3>
          <div className="space-y-1 max-h-96 overflow-y-auto pr-2">
            {lines.map((line, i) => (
              <p key={i} className="text-sm text-ink/80 leading-relaxed">
                {line}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scripts tab
// ---------------------------------------------------------------------------

function ScriptsTab({
  sessionId,
  projectId,
}: {
  sessionId: string;
  projectId: string;
}) {
  const { devHeaders } = useAuth();
  const [scripts, setScripts]   = useState<TestScript[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    testScriptsApi.list(projectId, devHeaders, { sessionId })
      .then((data) => {
        if (!cancelled) setScripts(data as TestScript[]);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load scripts');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpdate = async (id: string, field: string, value: string) => {
    if (field.startsWith('step_')) {
      const underIdx = field.indexOf('_', 5);
      const stepIndex = parseInt(field.slice(5, underIdx), 10);
      const stepField = field.slice(underIdx + 1);

      setScripts((prev) =>
        prev.map((s) => {
          if (s.id !== id) return s;
          const newSteps = (s.steps ?? []).map((st, i) =>
            i === stepIndex ? { ...st, [stepField]: value } : st,
          );
          return { ...s, steps: newSteps };
        }),
      );

      try {
        const currentScript = scripts.find((s) => s.id === id);
        const updatedSteps = (currentScript?.steps ?? []).map((st, i) => ({
          stepNumber:     st.step_number,
          description:    i === stepIndex && stepField === 'description' ? value : st.description,
          expectedResult: i === stepIndex && stepField === 'expected_result' ? value : (st.expected_result ?? undefined),
          notes:          i === stepIndex && stepField === 'notes' ? value : (st.notes ?? undefined),
        }));
        await testScriptsApi.update(id, { steps: updatedSteps }, devHeaders);
      } catch {
        const data = await testScriptsApi.list(projectId, devHeaders, { sessionId }) as TestScript[];
        setScripts(data);
      }
      return;
    }

    setScripts((prev) =>
      prev.map((s) => s.id === id ? { ...s, [field]: value } : s)
    );
    try {
      await testScriptsApi.update(id, { [field]: value }, devHeaders);
    } catch {
      const data = await testScriptsApi.list(projectId, devHeaders, { sessionId }) as TestScript[];
      setScripts(data);
    }
  };

  if (error) {
    return (
      <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm">
        <AlertCircle size={14} className="shrink-0" /> {error}
      </div>
    );
  }

  // Script creation now requires a UAT subcycle (see the UAT page's Cycles
  // tab) — this view only browses/edits the project's existing scripts.
  return (
    <ScriptTable
      scripts={scripts}
      loading={loading}
      onUpdate={handleUpdate}
    />
  );
}

// ---------------------------------------------------------------------------
// FDD tab
// ---------------------------------------------------------------------------

function FDDTab({ docId, devHeaders }: { docId: string | null; devHeaders: Record<string, string> }) {
  const [sections, setSections]   = useState<FDDSection[]>([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');

  useEffect(() => {
    if (!docId) return;
    let cancelled = false;
    setLoading(true);
    documentsApi.getSections(docId, devHeaders)
      .then((data) => { if (!cancelled) setSections(data as FDDSection[]); })
      .catch(() => { if (!cancelled) setSections([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [docId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!docId) {
    return (
      <div className="empty-state py-12">
        <FileText size={24} className="text-muted/40 mb-2 mx-auto" />
        <p className="text-sm text-muted">No FDD document exists for this project yet.</p>
        <p className="text-xs text-muted/70 mt-1">
          FDD documents can be generated and reviewed from the project's Documents page.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm">
        <AlertCircle size={14} className="shrink-0" /> {error}
      </div>
    );
  }

  const handleSave = async (key: string, contentMd: string) => {
    if (!docId) return;
    await documentsApi.updateSection(docId, key, { content_md: contentMd }, devHeaders);
    setSections((prev) =>
      prev.map((s) => s.section_key === key ? { ...s, content_md: contentMd } : s)
    );
  };

  const handleRegenerate = async (key: string) => {
    if (!docId) return;
    const updated = await documentsApi.regenSection(docId, key, {}, devHeaders) as FDDSection;
    setSections((prev) =>
      prev.map((s) => s.section_key === key ? { ...s, ...updated } : s)
    );
  };

  return (
    <FDDEditor
      docId={docId}
      sections={sections}
      loading={loading}
      onSave={handleSave}
      onRegenerate={handleRegenerate}
    />
  );
}

// ---------------------------------------------------------------------------
// SessionDetailPage
// ---------------------------------------------------------------------------

export function SessionDetailPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { devHeaders } = useAuth();

  const [session, setSession]   = useState<UatSession | null>(null);
  const [upload, setUpload]     = useState<AudioUpload | null>(null);
  const [fddDocId, setFddDocId] = useState<string | null>(null);
  const [loading, setLoading]   = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [error, setError]       = useState('');
  const [tab, setTab]           = useState<ActiveTab>('requirements');

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const sess = await uatApi.getSession(sessionId, devHeaders) as UatSession;
      setSession(sess);

      // Sessions have no FK to a specific audio upload or FDD document
      // (audio_uploads/transcriptions/documents are all project_id-scoped
      // only — see migrations 006/007/009) — show the project's most
      // recent of each instead.
      setUploadLoading(true);
      audioApi.list(sess.project_id, devHeaders)
        .then((data) => {
          const uploads = data as AudioUpload[];
          const latest = [...uploads].sort(
            (a, b) => new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime(),
          )[0];
          setUpload(latest ?? null);
        })
        .catch(() => setUpload(null))
        .finally(() => setUploadLoading(false));

      documentsApi.list(sess.project_id, devHeaders)
        .then((data) => {
          const docs = data as { id: string; doc_type: string }[];
          const fdd = docs.find((d) => d.doc_type === 'fdd');
          setFddDocId(fdd?.id ?? null);
        })
        .catch(() => setFddDocId(null));

      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load session');
    } finally {
      setLoading(false);
    }
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const statusIcon = {
    scheduled:   <Clock size={14} className="text-warning" />,
    in_progress: <Play size={14} className="text-blue" />,
    completed:   <CheckCircle size={14} className="text-success" />,
  }[session?.status ?? ''] ?? <Clock size={14} className="text-muted" />;

  const TABS: { key: ActiveTab; label: string; icon: React.ReactNode }[] = [
    { key: 'requirements', label: 'Requirements', icon: <Layers size={14} /> },
    { key: 'scripts',      label: 'Test Scripts', icon: <ClipboardList size={14} /> },
    { key: 'fdd',          label: 'FDD',          icon: <FileText size={14} /> },
  ];

  if (loading) {
    return (
      <div className="p-8 max-w-5xl space-y-4 animate-pulse">
        <div className="h-6 w-48 rounded bg-gray/60" />
        <div className="h-10 w-72 rounded bg-gray/60" />
        <div className="card h-64 bg-gray/60" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 text-[11px] font-mono text-muted mb-4">
        {session && (
          <>
            <Link
              to={`/projects/${session.project_id}/uat`}
              className="hover:text-blue transition-colors"
            >
              UAT
            </Link>
            <ChevronRight size={10} />
          </>
        )}
        <span className="text-ink">{session?.name ?? sessionId}</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="page-title">{session?.name ?? 'Session'}</h1>
            {session && <Badge variant={session.status as never} />}
          </div>
          {session && (
            <p className="text-xs font-mono text-muted mt-1 flex items-center gap-1.5">
              {statusIcon}
              {session.status.replace('_', ' ')}
              {' · '}
              {new Date(session.created_at).toLocaleDateString('en-US', {
                month: 'long', day: 'numeric', year: 'numeric',
              })}
            </p>
          )}
        </div>

        {session && (
          <Link
            to={`/projects/${session.project_id}/uat`}
            className="btn-ghost text-sm flex items-center gap-1.5"
          >
            <ArrowLeft size={14} /> Back to UAT
          </Link>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm mb-4">
          <AlertCircle size={14} className="shrink-0" /> {error}
        </div>
      )}

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-[rgba(8,17,31,0.08)] mb-4">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={[
              'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === t.key
                ? 'border-blue text-blue'
                : 'border-transparent text-muted hover:text-ink',
            ].join(' ')}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'requirements' && (
        <RequirementsTab upload={upload} loading={uploadLoading} />
      )}

      {tab === 'scripts' && session && (
        <ScriptsTab sessionId={session.id} projectId={session.project_id} />
      )}

      {tab === 'fdd' && (
        <FDDTab docId={fddDocId} devHeaders={devHeaders} />
      )}
    </div>
  );
}
